import { NavLink, Outlet, useLocation, useParams } from "react-router";
import { useEffect, useState } from "react";
import t from "@/lang/lang";
import {
  AirVent,
  Building,
  Building2,
  ChevronRight,
  Handshake,
  House,
  MapPinHouse,
} from "lucide-react";
import { useCustomersCustomerShowQuery } from "@/services/backend/customers/api/customer-api";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
} from "@/shared/components/ui/sidebar";
import { CustomerPageMain } from "@/routes/customers/[customerId]/_components/customer-page";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";

export function CustomerRoute() {
  const { customerId } = useParams();
  const { error } = useCustomersCustomerShowQuery({ id: customerId! });

  return (
    <SidebarProvider>
      <Navigation />
      {error && (
        <CustomerPageMain>
          <div className="p-4">
            <RTKQueryErrorAlert error={error} />
          </div>
        </CustomerPageMain>
      )}
      {!error && <Outlet />}
    </SidebarProvider>
  );
}

function Navigation() {
  return (
    <Sidebar className="pt-16" collapsible="icon">
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t("Allgemein")}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <NavLink to="address">
                  {({ isActive }) => (
                    <SidebarMenuButton isActive={isActive}>
                      <House className="mr-2 h-5 w-5" />
                      <span>{t("Adresse")}</span>
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <NavLink to="service-objects">
                  {({ isActive }) => (
                    <SidebarMenuButton isActive={isActive}>
                      <Building className="mr-2 h-5 w-5" />
                      <span>{t("Service Objekte")}</span>
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
              <BuildingsNavigationItem />
              <SidebarMenuItem>
                <NavLink to="relationships">
                  {({ isActive }) => (
                    <SidebarMenuButton isActive={isActive}>
                      <Handshake className="mr-2 h-5 w-5" />
                      <span>{t("Beziehungen")}</span>
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <NavLink to="business-central-contracts">
                  {({ isActive }) => (
                    <SidebarMenuButton isActive={isActive}>
                      <Handshake className="mr-2 h-5 w-5" />
                      <span>{t("Business Central Verträge")}</span>
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>{t("Dienstleistungen")}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <Collapsible
                asChild
                defaultOpen={false}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton>
                      <AirVent className="mr-2 h-5 w-5" />
                      <span>{t("HTZ")}</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <NavLink to="htz/ahus">
                          {({ isActive }) => (
                            <SidebarMenuSubButton isActive={isActive}>
                              <AirVent className="mr-2 h-5 w-5" />
                              <span>{t("RLT-Anlagen")}</span>
                            </SidebarMenuSubButton>
                          )}
                        </NavLink>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}

function BuildingsNavigationItem() {
  const { customerId } = useParams();
  const { pathname } = useLocation();
  const buildingsPath = `/customers/${customerId}/buildings`;
  const addressesPath = `${buildingsPath}/addresses`;
  const isBuildingsSection = pathname.startsWith(buildingsPath);
  const isAddressesSection = pathname.startsWith(addressesPath);
  const isBuildingsListSection = isBuildingsSection && !isAddressesSection;
  const [open, setOpen] = useState(isBuildingsSection);

  useEffect(() => {
    if (isBuildingsSection) {
      setOpen(true);
    }
  }, [isBuildingsSection]);

  return (
    <Collapsible
      asChild
      open={open}
      onOpenChange={setOpen}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <SidebarMenuButton asChild isActive={isBuildingsSection}>
          <NavLink to="buildings">
            <Building2 className="mr-2 h-5 w-5" />
            <span>{t("Gebäudeverwaltung")}</span>
          </NavLink>
        </SidebarMenuButton>
        <CollapsibleTrigger asChild>
          <SidebarMenuAction className="data-[state=open]:rotate-90">
            <ChevronRight className="transition-transform duration-200" />
            <span className="sr-only">{t("Gebäudeverwaltung umschalten")}</span>
          </SidebarMenuAction>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton asChild isActive={isBuildingsListSection}>
                <NavLink to="buildings" end>
                  <Building2 className="mr-2 h-4 w-4" />
                  <span>{t("Gebäude")}</span>
                </NavLink>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton asChild isActive={isAddressesSection}>
                <NavLink to="buildings/addresses">
                  <MapPinHouse className="mr-2 h-4 w-4" />
                  <span>{t("Adressen")}</span>
                </NavLink>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}
